import {
  type AIProviderId,
  aiProviderEndpointUrl,
  configurableAIProviders,
  getAIProvider,
  THREE_B_AI_PROVIDER_ID,
} from "../../../shared/aiProviders";
import { connectorTypeOf } from "../../../shared/connectors";
import { MANAGED_AI_CONNECTOR_TYPE } from "../../../shared/connectors/managedAi";
import { matchesUrlPattern } from "../../../shared/connectors/matchesUrlPattern";
import type { AIConfigDraft } from "./aiProviderDraft";

type ConnectorOption = { identifier: string; urlPattern: string };

function productVariants(provider: AIProviderId) {
  const identifier = getAIProvider(provider).connectorIdentifier;
  return identifier ? (connectorTypeOf(identifier)?.variants ?? []) : [];
}

// Only variants declaring `usage` serve a provider, since the proxy meters and
// model-filters exactly those; one backing a provider of its own is left to it
// (Bedrock's two auth variants both reach Bedrock).
function ownConnectorIdentifiers(provider: AIProviderId): string[] {
  const definition = getAIProvider(provider);
  // A 3B managed AI connector has no endpoint to match, only its type.
  if (provider === THREE_B_AI_PROVIDER_ID) {
    return [MANAGED_AI_CONNECTOR_TYPE.identifier];
  }
  if (!definition.endpointFromConnector) return [];
  const claimedElsewhere = new Set(
    configurableAIProviders
      .filter((candidate) => candidate.id !== provider)
      .map((candidate) => candidate.connectorIdentifier)
  );
  return productVariants(provider)
    .filter(
      (variant) =>
        variant.usage &&
        (variant.identifier === definition.connectorIdentifier ||
          !claimedElsewhere.has(variant.identifier))
    )
    .map((variant) => variant.identifier);
}

// A connector that owns the endpoint skips the URL check, since its pattern is
// scoped to its own base URL. A typed endpoint keeps the check: the proxy signs
// only requests matching a pattern. A product's unmetered modes stay out even
// when their pattern covers the endpoint.
export function candidateConnectors<T extends ConnectorOption>(
  draft: AIConfigDraft,
  connectors: readonly T[]
): T[] {
  const definition = getAIProvider(draft.provider);
  const endpointUrl = aiProviderEndpointUrl({
    provider: draft.provider,
    baseUrl: definition.packCredentials(draft.credentials).baseUrl,
  });
  const ownTypeIdentifiers = new Set(ownConnectorIdentifiers(draft.provider));
  const unmeteredSiblings = new Set(
    productVariants(draft.provider)
      .filter(
        (variant) =>
          !variant.usage &&
          variant.identifier !== definition.connectorIdentifier
      )
      .map((variant) => variant.identifier)
  );
  return connectors.filter(
    (connector) =>
      ownTypeIdentifiers.has(connector.identifier) ||
      (!unmeteredSiblings.has(connector.identifier) &&
        endpointUrl != null &&
        matchesUrlPattern(connector.urlPattern, endpointUrl))
  );
}
